import { isValidElement, type CSSProperties, type ReactNode } from "react"
import {
  filterFns,
  type ColumnDef,
  type Column,
  type ColumnSizingState,
  type FilterFn,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table"
import Icon from "@/v2-ui/icon"
import TooltipIcon from "@/v2-ui/tooltip/TooltipIcon"
import Link from "@/v2-backoffice-client/components/link"
import TableSelectionCheckbox from "./TableSelectionCheckbox"
import type {
  TableQueryParams,
  TableQueryFilter,
  ColumnMeta,
  ColumnMetaMap,
  ColumnFilterConfig,
  GroupingRange,
} from "./table.types"
import {
  INTERNAL_COLUMN_PREFIX,
  SELECTION_COLUMN_ID,
  SPACER_COLUMN_ID,
} from "./table.constants"
import { resolveRangeIndex } from "./table.grouping"
import { isStatusFilterValue } from "./TableStatusFilter"
import { isDateFilterValue } from "./filter/dateFilter"

/**
 * Filter function for number-range [min, max].
 * Passes if value is within the range (inclusive). Undefined bounds are ignored.
 */
const numberRangeFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
  const value = row.getValue<number>(columnId)
  if (value === null) return false
  const range = (filterValue as [number | undefined, number | undefined]) ?? []
  const min = range[0]
  const max = range[1]
  if (min !== undefined && value < min) return false
  if (max !== undefined && value > max) return false
  return true
}

/**
 * Filter function for select (exact match).
 */
const selectFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
  if (!filterValue) return true
  const value = row.getValue<string>(columnId)
  return String(value).toLowerCase() === String(filterValue).toLowerCase()
}

/**
 * Filter function for multi-select (row value is any of the selected values).
 */
const multiSelectFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
  if (!Array.isArray(filterValue) || filterValue.length === 0) return true
  const value = row.getValue<unknown>(columnId)
  const selected = new Set(filterValue.map((v) => String(v).toLowerCase()))
  return selected.has(String(value).toLowerCase())
}

/**
 * Filter function for boolean.
 */
const booleanFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
  if (filterValue === undefined || filterValue === null) return true
  const value = row.getValue<boolean>(columnId)
  return value === filterValue
}

/**
 * Filter function for date-filter (within/before/after with from/to ISO strings).
 */
const dateFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
  if (!filterValue || typeof filterValue !== "object") return true
  const cellValue = row.getValue<string>(columnId)
  if (!cellValue) return false
  const cellDate = new Date(cellValue).getTime()
  if (Number.isNaN(cellDate)) return false
  const { from, to } = filterValue as { from?: string; to?: string }
  if (from && cellDate < new Date(from).getTime()) return false
  if (to && cellDate > new Date(to).getTime()) return false
  return true
}

/**
 * Returns the appropriate filter function for a given filter config.
 */
function getFilterFn(
  filter?: ColumnFilterConfig,
): FilterFn<unknown> | "includesString" | undefined {
  if (!filter) return undefined
  switch (filter.type) {
    case "number-range": {
      return numberRangeFilterFn
    }
    case "select": {
      return selectFilterFn
    }
    case "multi-select": {
      return multiSelectFilterFn
    }
    case "boolean": {
      return booleanFilterFn
    }
    case "date-filter": {
      return dateFilterFn
    }
    default: {
      return "includesString"
    }
  }
}

/**
 * Table.StatusFilter sets a StatusFilterValue-wrapped column filter, whatever
 * the table's filtering mode. manualFiltering tables (Users, Tenants, ...)
 * never run this: buildQueryParams unwraps it into a server query param
 * before TanStack's own filtered-row-model would apply it. Client-side
 * tables have no such interception, so their column filterFn must unwrap it
 * itself, or the pill silently filters nothing (the wrapped object stringifies
 * to "[object Object]" against every built-in/custom filterFn here). Falls
 * through to the column's own configured filterFn for every other value.
 */
function withStatusFilterSupport<TData>(
  base: FilterFn<TData> | "includesString",
): FilterFn<TData> {
  const baseFn: FilterFn<TData> =
    base === "includesString"
      ? (filterFns.includesString as FilterFn<TData>)
      : base
  return (row, columnId, filterValue, addMeta) => {
    if (isStatusFilterValue(filterValue)) {
      const cellValue = row.getValue(columnId)
      return String(cellValue).toLowerCase() === filterValue.eq.toLowerCase()
    }
    return baseFn(row, columnId, filterValue, addMeta)
  }
}

const EMPTY_VALUE = "—"
const BLACKLISTED_KEYS = new Set(["__typename"])

type CreateColumnsInput<TData> = {
  columns: ColumnMetaMap<TData>
  enableSelection?: boolean
  enableSorting?: boolean
  enableHiding?: boolean
}

/**
 * Creates column definitions from a metadata map.
 */
export function createColumns<TData extends Record<string, unknown>>(
  input: CreateColumnsInput<TData>,
): ColumnDef<TData, unknown>[] {
  const {
    columns: columnMetaMap,
    enableSelection = false,
    enableSorting = true,
    enableHiding = true,
  } = input

  const result: ColumnDef<TData, unknown>[] = []

  if (enableSelection) {
    result.push(createSelectionColumn<TData>())
  }

  for (const [key, meta] of Object.entries(columnMetaMap)) {
    if (BLACKLISTED_KEYS.has(key)) continue
    result.push(createColumn<TData>(key, meta, { enableSorting, enableHiding }))
  }

  return result
}

function createSelectionColumn<TData>(): ColumnDef<TData, unknown> {
  return {
    id: SELECTION_COLUMN_ID,
    size: 30,
    minSize: 30,
    maxSize: 30,
    enableSorting: false,
    enableHiding: false,
    enableGrouping: false,
    enableResizing: false,
    header({ table }) {
      return (
        <input
          type="checkbox"
          className="ui-table-selection-checkbox"
          aria-label="Select all"
          checked={table.getIsAllPageRowsSelected()}
          onChange={table.getToggleAllPageRowsSelectedHandler()}
          ref={(el) => {
            if (el) el.indeterminate = table.getIsSomePageRowsSelected()
          }}
        />
      )
    },
    cell(cell) {
      // A busy row (background write in flight) shows a spinner instead of
      // its checkbox: the operator sees exactly which rows are being worked
      // on rather than a seemingly frozen table.
      if (cell.table.options.meta?.busyRowIds?.has(cell.row.id)) {
        // Same box model as .ui-table-selection-checkbox (size-4 m-1) so the
        // spinner sits exactly where the checkbox it replaces would be,
        // rather than a smaller, unmargined icon shifted toward the corner.
        return (
          <Icon
            loading
            size={16}
            className="text-muted-foreground m-1"
            aria-label="Working…"
          />
        )
      }
      return (
        <TableSelectionCheckbox
          className="ui-table-selection-checkbox"
          row={cell.row}
        />
      )
    },
  }
}

function buildRangeGroupingFn<TData>(
  key: string,
  getValue: ((row: TData) => unknown) | undefined,
  ranges: GroupingRange[],
): (row: TData) => number {
  return (row: TData) => {
    const value = getValue
      ? getValue(row)
      : (row as Record<string, unknown>)[key]
    return resolveRangeIndex(value, ranges)
  }
}

function createColumn<TData>(
  key: string,
  meta: ColumnMeta<TData>,
  options: { enableSorting: boolean; enableHiding: boolean },
): ColumnDef<TData, unknown> {
  const { enableSorting, enableHiding } = options
  const headerLabel = meta.label ?? key
  const accessor:
    | { accessorKey: string }
    | { accessorFn: (row: TData) => unknown } = meta.getValue
    ? { accessorFn: (row: TData) => meta.getValue!(row) }
    : { accessorKey: key }

  const ranges = meta.groupingRanges
  const canGroup = ranges?.length
    ? true
    : meta.getGroupingValue
      ? true
      : (meta.enableGrouping ?? false)

  return {
    id: key,
    ...accessor,
    size: meta.defaultSize,
    enableSorting: meta.enableSorting ?? enableSorting,
    // Virtual columns can never be toggled visible — getCanHide() false also
    // keeps them out of the columns panel.
    enableHiding: meta.virtual ? false : (meta.enableHiding ?? enableHiding),
    enableGrouping: canGroup,
    enableColumnFilter: true,
    filterFn: withStatusFilterSupport(
      (getFilterFn(meta.filter) ?? "includesString") as
        | FilterFn<TData>
        | "includesString",
    ),
    ...(meta.sortingFn && { sortingFn: meta.sortingFn }),
    ...(ranges?.length
      ? { getGroupingValue: buildRangeGroupingFn(key, meta.getValue, ranges) }
      : meta.getGroupingValue
        ? { getGroupingValue: meta.getGroupingValue }
        : {}),
    meta: {
      id: key,
      label: meta.label ?? key,
      description: meta.description,
      className: meta.className,
      canGroup,
      groupingRanges: meta.groupingRanges,
      renderGroupLabel: meta.renderGroupLabel,
      filter: meta.filter,
      defaultHidden: meta.defaultHidden,
      virtual: meta.virtual,
      isWrapped: meta.isWrapped,
      exportValue: meta.exportValue,
      summaryValue: meta.summaryValue,
    },
    header() {
      // A column with a `description` gets a small info icon after its label,
      // explaining what the column means to a first-time reader. Keeping the
      // tooltip on a dedicated icon (rather than wrapping the whole label)
      // leaves the header label readable and uncluttered. Hovering the icon
      // reveals the description; the th's sort click still fires through.
      if (!meta.description) return headerLabel
      return (
        <span className="ui-table-header-label">
          {headerLabel}
          <TooltipIcon
            iconSrc="info"
            iconSize={12}
            iconClassName="ui-table-header-tooltip-icon"
            // The --panel shell is padding-less by design (padding lives on
            // inner sections), so pad the plain-text description ourselves.
            content={
              <span className="block px-3 py-2">{meta.description}</span>
            }
            contentSide="top"
            contentClassName="ui-tooltip-content--panel max-w-[280px]"
          />
        </span>
      )
    },
    cell(cell) {
      const value = cell.getValue()

      let content: string | ReactNode = EMPTY_VALUE

      if (meta.renderCell) {
        const rendered = meta.renderCell({
          key,
          column: cell.column,
          row: cell.row,
          value,
        })
        if (rendered === null || rendered === undefined) {
          content = EMPTY_VALUE
        } else if (isValidElement(rendered)) {
          content = <span className="ui-table-cell-value">{rendered}</span>
        } else {
          content = rendered
        }
      } else {
        content = value === null || value === "" ? EMPTY_VALUE : String(value)
      }

      const hasValue =
        content !== null && content !== EMPTY_VALUE && content !== ""

      const renderInput = { key, column: cell.column, row: cell.row, value }

      if (meta.to && hasValue) {
        const to =
          typeof meta.to === "function" ? meta.to(renderInput) : meta.to
        if (to) {
          return (
            <Link
              to={to}
              className="ui-table-cell-link"
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              {content as ReactNode}
            </Link>
          )
        }
      }

      if (meta.onClick && hasValue) {
        return (
          <button
            type="button"
            className="ui-link ui-table-cell-button"
            onClick={(e) => {
              e.stopPropagation()
              meta.onClick?.(renderInput)
            }}
          >
            {content as ReactNode}
          </button>
        )
      }

      return content as ReactNode
    },
  }
}

/**
 * The rows auto-fit sizes a column to: header and body. The summary row is
 * left out on purpose — a list of every value in a column would otherwise
 * stretch it to the width of that list, when the summary truncates and
 * reveals in full on hover by design.
 */
const MEASURED_ROWS_SELECTOR = ":scope > :is(thead, tbody) > tr"

/**
 * Measures the natural (auto-fit) width for a column by cloning its cells
 * into an off-screen table with `table-layout: auto`, where cells shrink
 * to their content width rather than being constrained by the fixed layout.
 */
export function measureNaturalColumnWidth(
  tableEl: HTMLTableElement,
  colIndex: number,
): number {
  const cells = tableEl.querySelectorAll(
    `${MEASURED_ROWS_SELECTOR} > :nth-child(${colIndex + 1})`,
  )
  if (cells.length === 0) return 0

  const wrapper = document.createElement("div")
  wrapper.style.cssText =
    "position:fixed;top:-9999px;left:-9999px;visibility:hidden;pointer-events:none;"

  const measure = document.createElement("table")
  measure.className = tableEl.className
  measure.style.tableLayout = "auto"
  measure.style.width = "auto"
  measure.style.minWidth = "0"

  for (const cell of cells) {
    const el = cell as HTMLElement
    const tr = document.createElement("tr")
    const td = document.createElement(el.tagName.toLowerCase()) as HTMLElement
    td.className = el.className
    td.innerHTML = el.innerHTML
    td.style.width = "auto"
    td.style.minWidth = "0"
    td.style.maxWidth = "none"
    td.style.whiteSpace = "nowrap"
    td.style.overflow = "visible"
    tr.appendChild(td)
    measure.appendChild(tr)
  }

  wrapper.appendChild(measure)
  document.body.appendChild(wrapper)

  let maxWidth = 0
  for (const cell of measure.querySelectorAll("td, th")) {
    maxWidth = Math.max(maxWidth, (cell as HTMLElement).offsetWidth)
  }

  wrapper.remove()
  return Math.ceil(maxWidth)
}

/**
 * Measures the natural (auto-fit) width for multiple columns in a single layout
 * pass. Creates one off-screen clone table with only the requested columns,
 * appends it once, reads all widths, then removes it.
 *
 * Returns a Map from DOM column index → natural pixel width.
 */
export function measureAllNaturalColumnWidths(
  tableEl: HTMLTableElement,
  colIndices: number[],
): Map<number, number> {
  if (colIndices.length === 0) return new Map()

  const rows = tableEl.querySelectorAll(MEASURED_ROWS_SELECTOR)
  if (rows.length === 0) return new Map()

  const wrapper = document.createElement("div")
  wrapper.style.cssText =
    "position:fixed;top:-9999px;left:-9999px;visibility:hidden;pointer-events:none;"

  const measure = document.createElement("table")
  measure.className = tableEl.className
  measure.style.tableLayout = "auto"
  measure.style.width = "auto"
  measure.style.minWidth = "0"

  for (const row of rows) {
    const children = row.children
    const tr = document.createElement("tr")

    for (const colIndex of colIndices) {
      if (colIndex >= children.length) {
        tr.appendChild(document.createElement("td"))
        continue
      }

      const el = children[colIndex] as HTMLElement
      const td = document.createElement(el.tagName.toLowerCase()) as HTMLElement
      td.className = el.className
      td.innerHTML = el.innerHTML
      td.style.width = "auto"
      td.style.minWidth = "0"
      td.style.maxWidth = "none"
      td.style.whiteSpace = "nowrap"
      td.style.overflow = "visible"
      tr.appendChild(td)
    }

    measure.appendChild(tr)
  }

  wrapper.appendChild(measure)
  document.body.appendChild(wrapper)

  const result = new Map<number, number>()
  const clonedRows = measure.querySelectorAll("tr")
  for (let cellIdx = 0; cellIdx < colIndices.length; cellIdx++) {
    let maxWidth = 0
    for (const row of clonedRows) {
      const cell = row.children[cellIdx] as HTMLElement | undefined
      if (cell) {
        maxWidth = Math.max(maxWidth, cell.offsetWidth)
      }
    }
    result.set(colIndices[cellIdx], Math.ceil(maxWidth))
  }

  wrapper.remove()
  return result
}

export function getColumnTitle<TData>(column: Column<TData, unknown>): string {
  const meta = column.columnDef.meta as ColumnMeta<TData> | undefined
  return meta?.label ?? column.id
}

/** True for the columns the table adds itself (selection, actions, spacer). */
export function isInternalColumnId(columnId: string): boolean {
  return columnId.startsWith(INTERNAL_COLUMN_PREFIX)
}

const UNSIZED_COLUMN_STYLE: CSSProperties = {}

/**
 * The inline width a cell of `column` renders with — shared by the header,
 * body and summary rows so the three always agree on a column's width (a
 * resizable table lays out with `width: auto`, where every row's cells take
 * part in sizing). Every column of a resizable (persisted) table is fixed
 * except the spacer that absorbs the remaining width; otherwise only columns
 * sized explicitly, by their definition or by the operator, are.
 */
export function getColumnSizeStyle<TData>(input: {
  column: Column<TData, unknown>
  columnSizing: ColumnSizingState
  hasPersistence: boolean
}): CSSProperties {
  const { column, columnSizing, hasPersistence } = input
  const isSpacer = column.id === SPACER_COLUMN_ID
  const hasFixedSize =
    (hasPersistence && !isSpacer) ||
    column.columnDef.size !== undefined ||
    columnSizing[column.id] !== undefined
  if (!hasFixedSize) return UNSIZED_COLUMN_STYLE

  const width = column.getSize()
  return { width, minWidth: width, maxWidth: width }
}

export function canColumnGroup<TData>(column: Column<TData, unknown>): boolean {
  if (column.id === SELECTION_COLUMN_ID) return false
  if (!column.getCanGroup()) return false
  const meta = column.columnDef.meta as { canGroup?: boolean } | undefined
  return meta?.canGroup === true
}

export function getDefaultColumnVisibility<TData>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _columns: ColumnDef<TData, unknown>[],
): VisibilityState {
  return {}
}

/**
 * Converts internal TanStack state to server-ready query params.
 * Emits the object-based structure matching the GraphQL ListQuerySort/ListQueryFilter inputs.
 */
export function buildQueryParams(
  sorting: SortingState,
  columnFilters: ColumnFiltersState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: { id: string; columnDef: { meta?: any } }[],
): TableQueryParams {
  const params: TableQueryParams = {}

  // Sorting (preserves multi-sort priority order)
  if (sorting.length > 0) {
    params.sort = sorting.map((s) => ({
      column: s.id,
      direction: s.desc ? "desc" : "asc",
    }))
  }

  // Filters
  if (columnFilters.length > 0) {
    const filters: TableQueryFilter[] = []

    for (const cf of columnFilters) {
      const value = cf.value
      if (value === undefined || value === null) continue

      if (isStatusFilterValue(value)) {
        filters.push({ column: cf.id, eq: value.eq })
        continue
      }

      if (isDateFilterValue(value)) {
        const filter: TableQueryFilter = { column: cf.id }
        if (value.start) filter.gte = value.start
        if (value.end) filter.lte = value.end
        if (filter.gte || filter.lte) {
          filters.push(filter)
        }
        continue
      }

      // Look up filter type from column meta to determine operator
      const col = columns.find((c) => c.id === cf.id)
      const filterConfig = col?.columnDef.meta?.filter as
        | ColumnFilterConfig
        | undefined

      // Multi-select is also an array, so it must be checked before the
      // number-range branch below (which assumes [min, max]).
      if (filterConfig?.type === "multi-select") {
        const values = (Array.isArray(value) ? value : []).map(String)
        if (values.length > 0) filters.push({ column: cf.id, in: values })
        continue
      }

      if (Array.isArray(value)) {
        // Number range: [min, max]
        const [min, max] = value as [number | undefined, number | undefined]
        if (min !== undefined && max !== undefined) {
          filters.push({
            column: cf.id,
            gte: String(min),
            lte: String(max),
          })
        } else if (min !== undefined) {
          filters.push({ column: cf.id, gte: String(min) })
        } else if (max !== undefined) {
          filters.push({ column: cf.id, lte: String(max) })
        }
      } else if (
        filterConfig?.type === "select" ||
        filterConfig?.type === "boolean"
      ) {
        filters.push({ column: cf.id, eq: String(value) })
      } else {
        // Text / default → contains
        filters.push({ column: cf.id, contains: String(value) })
      }
    }

    if (filters.length > 0) {
      params.filters = filters
    }
  }

  return params
}
